import { api } from './api';
import type {
  Budget,
  BudgetHistoryPoint,
  BudgetUpsert,
  Credential,
  CredentialCreate,
  CredentialReveal,
  CredentialRotate,
  CredentialUpdate,
  ProviderSetting,
  WorkflowConfigOverlay,
  WorkflowConfigUpsert,
  WpSite,
  WpSiteCreate,
  WpSiteTestResult,
  WpSiteUpdate,
} from '@/types/config';

export const sitesApi = {
  list: (activeOnly = false) =>
    api.get<WpSite[]>(
      `/config/sites${activeOnly ? '?active_only=true' : ''}`,
    ),
  get: (id: string) => api.get<WpSite>(`/config/sites/${id}`),
  create: (payload: WpSiteCreate) => api.post<WpSite>('/config/sites', payload),
  update: (id: string, payload: WpSiteUpdate) =>
    api.put<WpSite>(`/config/sites/${id}`, payload),
  remove: (id: string) =>
    api.delete<{ deleted: boolean }>(`/config/sites/${id}`),
  test: (id: string) =>
    api.post<WpSiteTestResult>(`/config/sites/${id}/test`),
};

export const credentialsApi = {
  list: (provider?: string) =>
    api.get<Credential[]>(
      `/config/credentials${provider ? `?provider=${provider}` : ''}`,
    ),
  get: (id: string) => api.get<Credential>(`/config/credentials/${id}`),
  create: (payload: CredentialCreate) =>
    api.post<Credential>('/config/credentials', payload),
  update: (id: string, payload: CredentialUpdate) =>
    api.put<Credential>(`/config/credentials/${id}`, payload),
  remove: (id: string) =>
    api.delete<{ deleted: boolean }>(`/config/credentials/${id}`),
  rotate: (id: string, payload: CredentialRotate) =>
    api.put<Credential>(`/config/credentials/${id}/rotate`, payload),
  reveal: (id: string) =>
    api.get<CredentialReveal>(`/config/credentials/${id}/reveal`),
};

export const providersApi = {
  list: () => api.get<ProviderSetting[]>('/config/providers'),
  get: (provider: string) =>
    api.get<ProviderSetting>(`/config/providers/${provider}`),
  upsert: (provider: string, config: Record<string, unknown>) =>
    api.put<ProviderSetting>(`/config/providers/${provider}`, { config }),
};

export const workflowConfigApi = {
  get: (workflowId: string) =>
    api.get<WorkflowConfigOverlay | null>(
      `/config/workflows/${workflowId}`,
    ),
  upsert: (workflowId: string, payload: WorkflowConfigUpsert) =>
    api.put<WorkflowConfigOverlay>(
      `/config/workflows/${workflowId}`,
      payload,
    ),
};

export const budgetsApi = {
  list: () => api.get<Budget[]>('/config/budgets'),
  upsert: (provider: string, payload: BudgetUpsert) =>
    api.put<Budget>(`/config/budgets/${provider}`, payload),
  history: (months = 6) =>
    api.get<BudgetHistoryPoint[]>(`/config/budgets/history?months=${months}`),
};
